// SelfieSheet — explicit state machine, user-tapped shutter (E4 + E9 + F2).
//
// History: the previous version auto-clicked the hidden file input via
// setTimeout(0) whenever stage hit 'idle' while open. iOS Safari ignores
// programmatic .click() on a file input outside a synchronous user-gesture
// handler. The OS sometimes surfaced the camera picker anyway (Safari is
// lenient inside an in-flight tap chain) but the user reported the camera
// not firing on real iPhones — exactly the gesture violation codex flagged.
//
// E4 fix: drop the auto-click. The sheet opens to a 'ready' stage with a
// big shutter button. Tapping that button calls inputRef.current.click()
// SYNCHRONOUSLY inside the click handler — the only path iOS reliably
// honors. Cooldown drops from 60s → 5s so the next drink can re-prompt.
//
// E9 fix: filter no longer blocks the polaroid. State machine:
//   pick file → 'preview' with RAW image URL → polaroid paints →
//   requestAnimationFrame → microtask kicks off processStumblePhoto →
//   on completion swap polaroid src to the filtered blob URL.
//
// F2 fix: KEEP closes the sheet INSTANTLY (<100ms). The upload runs in the
// background via the IndexedDB-backed uploadQueue. If the filter is still
// running when KEEP fires, we race it against a 5s ceiling — filter wins
// most of the time (~600ms), fallback is raw bytes. Either way the user
// has already moved on. The drink logger route listens for the
// 'stumble:media-saved' / 'media-failed' events to refresh + toast.
//
// State machine:
//   idle       → sheet closed
//   ready      → sheet OPEN, shutter button visible, NO auto-click
//   capturing  → shutter just tapped, native picker open
//   preview    → photo captured, polaroid showing (raw → filtered swap)
//   done       → KEEP tapped — sheet has already closed (terminal)
//   error      → message + Try again (returns to ready)
//
// Mobile viewport rules (CLAUDE.md §5):
//   - position: fixed, inset: 0 — full-screen takeover.
//   - top padding uses env(safe-area-inset-top); bottom uses --safe-bottom.
//   - all touch targets ≥ 44pt.

import * as Sentry from '@sentry/browser'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type MediaDto, SELFIE_SAVED_EVENT } from '../api'
import { processStumblePhoto } from '../lib/media/stumbleCam'
import { queueUpload } from '../lib/media/uploadQueue'
import { Icon } from './Icon'
import { Polaroid } from './Polaroid'

// G1.4: breadcrumb every state machine transition so the next "selfie stuck"
// report has a trail. Wrapped in try/catch — Sentry must never break the
// sheet. No-op if Sentry isn't initialised (browser support, etc).
function breadcrumb(message: string, data?: Record<string, unknown>): void {
  try {
    Sentry.addBreadcrumb({
      category: 'selfie-sheet',
      message,
      level: 'info',
      data,
    })
  } catch {
    // swallow — observability must not break UX
  }
}

export type SelfieSheetProps = {
  open: boolean
  nightId: string
  venueName?: string
  city?: string
  drinkType?: string
  onClose: () => void
  /**
   * Fired when KEEP is tapped — the upload has been QUEUED, not necessarily
   * completed. Async upload completion is signalled via the global
   * 'stumble:media-saved' / 'stumble:media-failed' events on window — see
   * apps/web/src/lib/media/uploadQueue.ts. Parent routes listen for those.
   * The optional `media` is undefined when called from the queue path.
   */
  onUploaded: (mediaAsset?: MediaDto) => void
  // When true (default), the sheet listens for the drink-logged event and
  // auto-opens. Pass false to disable in tests / contexts that drive open
  // exclusively from the parent.
  autoTrigger?: boolean
  // Debounce window (ms) after the user explicitly dismisses; auto-trigger is
  // suppressed inside this window. Default 5s — user tested at 60s and felt
  // it suppressed prompts they wanted; 5s is just long enough to debounce
  // double-fires from a single drink log.
  autoTriggerCooldownMs?: number
  // Optional: parent gets notified so it can flip its own open state when the
  // sheet auto-opens. Without this the sheet manages open state internally.
  onAutoOpen?: () => void
}

type Stage =
  | { kind: 'idle' }
  | { kind: 'ready' }
  | { kind: 'capturing' }
  | {
      kind: 'preview'
      previewUrl: string
      filtered: boolean
      rawBlob: Blob
      filteredBlob: Blob | null
      // Promise that resolves to the filtered result (or rejects). Started at
      // pick time. handleKeep races it against a 5s ceiling.
      filterPromise: Promise<{ blob: Blob } | null>
    }
  | { kind: 'done' }
  | { kind: 'error'; message: string }

const DEFAULT_AUTO_COOLDOWN_MS = 5_000

export const DRINK_LOGGED_EVENT_NAME = 'stumble:drink-logged'
export const SELFIE_SAVED_EVENT_NAME = SELFIE_SAVED_EVENT

export function SelfieSheet({
  open,
  nightId,
  venueName,
  city,
  drinkType,
  onClose,
  onUploaded,
  autoTrigger = true,
  autoTriggerCooldownMs = DEFAULT_AUTO_COOLDOWN_MS,
  onAutoOpen,
}: SelfieSheetProps) {
  // Internal "auto-open" flag — only used when the parent doesn't drive
  // open=true itself (i.e. when onAutoOpen is undefined). The effective open
  // is `open || internalOpen`.
  const [internalOpen, setInternalOpen] = useState(false)
  const [stage, setStageRaw] = useState<Stage>({ kind: 'idle' })
  // setStage wrapper: drops a Sentry breadcrumb on every transition so the
  // next "selfie stuck" report has a state-machine trail. Accepts the same
  // signature as React's setter (value OR updater fn). Breadcrumbs only
  // capture the kind, never user data (no blob, no preview URL).
  const setStage = useCallback((next: Stage | ((prev: Stage) => Stage)) => {
    setStageRaw((prev) => {
      const resolved = typeof next === 'function' ? (next as (p: Stage) => Stage)(prev) : next
      if (prev.kind !== resolved.kind) {
        breadcrumb(`state→${resolved.kind}`, { from: prev.kind })
      }
      return resolved
    })
  }, [])
  const inputRef = useRef<HTMLInputElement | null>(null)
  const previewUrlsRef = useRef<Set<string>>(new Set())
  const lastDismissedAtRef = useRef<number>(0)

  const effectiveOpen = open || internalOpen

  const trackUrl = useCallback((url: string) => {
    previewUrlsRef.current.add(url)
  }, [])

  const revokeAllUrls = useCallback(() => {
    for (const url of previewUrlsRef.current) URL.revokeObjectURL(url)
    previewUrlsRef.current.clear()
  }, [])

  const closeAndRecord = useCallback(() => {
    breadcrumb('closeAndRecord')
    revokeAllUrls()
    setStage({ kind: 'idle' })
    if (inputRef.current) inputRef.current.value = ''
    lastDismissedAtRef.current = Date.now()
    setInternalOpen(false)
    onClose()
  }, [onClose, revokeAllUrls, setStage])

  // ESC key closes the sheet (a11y). Only attach while open.
  useEffect(() => {
    if (!effectiveOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeAndRecord()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [effectiveOpen, closeAndRecord])

  // Auto-trigger: listen for the drink-logged event the drink logger dispatches
  // after every successful POST. Debounce by `autoTriggerCooldownMs` after the
  // most recent dismissal so a user who said "no, not now" doesn't get spammed
  // on the next +1. We DO NOT auto-fire the camera — we just open the sheet
  // at stage='ready' and let the user tap the shutter.
  useEffect(() => {
    if (!autoTrigger) return
    function handler() {
      const now = Date.now()
      if (now - lastDismissedAtRef.current < autoTriggerCooldownMs) {
        breadcrumb('auto-trigger-debounced', {
          msSinceDismiss: now - lastDismissedAtRef.current,
          cooldownMs: autoTriggerCooldownMs,
        })
        return
      }
      if (effectiveOpen) {
        breadcrumb('auto-trigger-already-open')
        return
      }
      // Move to 'ready' so the shutter button renders. The shutter handler
      // is the only place .click() is called on the file input.
      breadcrumb('auto-trigger-opening')
      setStage({ kind: 'ready' })
      if (onAutoOpen) onAutoOpen()
      else setInternalOpen(true)
    }
    window.addEventListener(DRINK_LOGGED_EVENT_NAME, handler)
    return () => window.removeEventListener(DRINK_LOGGED_EVENT_NAME, handler)
  }, [autoTrigger, autoTriggerCooldownMs, effectiveOpen, onAutoOpen, setStage])

  // When the parent opens us (open=true) while we're idle, bump to ready.
  useEffect(() => {
    if (open && stage.kind === 'idle') setStage({ kind: 'ready' })
  }, [open, stage.kind, setStage])

  // Revoke object URLs on unmount.
  useEffect(() => {
    return () => {
      revokeAllUrls()
    }
  }, [revokeAllUrls])

  // Shutter handler — fires the hidden file input. MUST run synchronously
  // inside the click handler so iOS Safari accepts it as a user-gesture
  // descendant. NO setTimeout, NO microtask, NO defer.
  function handleShutter() {
    breadcrumb('shutter-tapped')
    if (!inputRef.current) {
      breadcrumb('shutter-noop-no-input-ref')
      return
    }
    setStage({ kind: 'capturing' })
    inputRef.current.click()
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) {
      // User cancelled the native picker — go back to ready so they can
      // tap the shutter again. No close — they're still in the sheet.
      breadcrumb('file-picker-cancelled')
      setStage({ kind: 'ready' })
      return
    }
    breadcrumb('file-picked', { sizeKb: Math.round(file.size / 1024), type: file.type })
    void handlePickedFile(file)
  }

  function handlePickedFile(file: File) {
    // Step 1: render the polaroid IMMEDIATELY with the raw image. The user
    // sees their shot before the filter pipeline burns a single cycle.
    const rawUrl = URL.createObjectURL(file)
    trackUrl(rawUrl)

    // Step 2: kick off the filter pipeline RIGHT AWAY but as a microtask so
    // the polaroid still paints first via rAF. We keep a reference to the
    // promise on the stage so handleKeep can race it against a ceiling
    // without re-triggering the pipeline.
    const filterPromise = new Promise<{ blob: Blob } | null>((resolve) => {
      requestAnimationFrame(() => {
        void Promise.resolve().then(async () => {
          try {
            const result = await processStumblePhoto(file, {
              photoId: crypto.randomUUID(),
              timestamp: new Date(),
              venueName,
              city,
              palette: 'poster',
              border: 'none',
            })
            // Swap polaroid preview to the filtered URL if we're still in
            // preview (user might have already tapped KEEP / closed).
            const filteredUrl = URL.createObjectURL(result.filtered)
            trackUrl(filteredUrl)
            setStage((prev) => {
              if (prev.kind !== 'preview') return prev
              return {
                ...prev,
                previewUrl: filteredUrl,
                filtered: true,
                filteredBlob: result.filtered,
              }
            })
            resolve({ blob: result.filtered })
          } catch (err) {
            // Filter failure is non-fatal — the polaroid stays on the raw
            // image; KEEP will upload raw bytes via the fallback in handleKeep.
            console.warn('[selfie] filter pipeline failed:', err)
            resolve(null)
          }
        })
      })
    })

    setStage({
      kind: 'preview',
      previewUrl: rawUrl,
      filtered: false,
      rawBlob: file,
      filteredBlob: null,
      filterPromise,
    })
  }

  function handleRetake() {
    revokeAllUrls()
    if (inputRef.current) inputRef.current.value = ''
    setStage({ kind: 'ready' })
  }

  function handleKeep() {
    breadcrumb('keep-tapped', {
      stage: stage.kind,
      filtered: stage.kind === 'preview' ? stage.filtered : false,
    })
    if (stage.kind !== 'preview') {
      breadcrumb('keep-noop-wrong-stage', { stage: stage.kind })
      return
    }
    const snapshot = stage

    // F2: sheet closes IMMEDIATELY. The upload runs in the background via the
    // IndexedDB-backed uploadQueue. Filter race happens after the sheet is
    // gone so the user isn't held hostage by ~600ms of canvas math.
    setStage({ kind: 'done' })
    // Caller notification — the upload itself completes async, so we hand
    // `undefined` for the media payload. Parent routes that need the
    // server-side MediaDto subscribe to MEDIA_SAVED_EVENT on window instead.
    onUploaded(undefined)
    closeAndRecord()

    // Background pipeline — wait for the filter (with a 5s ceiling) so the
    // blob we ship matches what the user just saw. If the filter blows past
    // the ceiling we upload the raw bytes; the polaroid frame is added by
    // the React Polaroid wrapper anyway, not baked into bytes.
    const FILTER_CEILING_MS = 5_000
    void (async () => {
      const timeoutToken = Symbol('filter-timeout')
      let outcome: { blob: Blob } | null | typeof timeoutToken
      try {
        outcome = await Promise.race<{ blob: Blob } | null | typeof timeoutToken>([
          snapshot.filterPromise,
          new Promise<typeof timeoutToken>((resolve) =>
            setTimeout(() => resolve(timeoutToken), FILTER_CEILING_MS),
          ),
        ])
      } catch (err) {
        console.warn('[stumble:upload] filter race threw — uploading raw:', err)
        outcome = null
      }
      let blobToUpload: Blob
      let usedFilter: boolean
      if (outcome === timeoutToken) {
        console.warn('[stumble:upload] filter timeout — uploaded raw bytes')
        breadcrumb('filter-timeout-raw-bytes', { ceilingMs: FILTER_CEILING_MS })
        blobToUpload = snapshot.rawBlob
        usedFilter = false
      } else if (outcome && 'blob' in outcome) {
        breadcrumb('filter-won', { sizeKb: Math.round(outcome.blob.size / 1024) })
        blobToUpload = outcome.blob
        usedFilter = true
      } else {
        breadcrumb('filter-null-raw-bytes')
        blobToUpload = snapshot.rawBlob
        usedFilter = false
      }
      void usedFilter // surfaced via the queue's filterPreset tag; kept for log clarity
      try {
        await queueUpload(blobToUpload, nightId, 'selfie')
        if (typeof window !== 'undefined') {
          // SELFIE_SAVED_EVENT is the legacy "selfie queued" signal (drives
          // older UI consumers); MEDIA_SAVED_EVENT will fire later once the
          // queue's POST returns.
          window.dispatchEvent(
            new CustomEvent(SELFIE_SAVED_EVENT_NAME, {
              detail: { nightId, mediaId: null },
            }),
          )
        }
      } catch (err) {
        // queueUpload itself shouldn't throw (it persists and processes
        // async), but log defensively so a future regression is visible.
        console.error('[stumble:upload] queueUpload threw:', err)
      }
    })()
  }

  function handleRetryFromError() {
    setStage({ kind: 'ready' })
  }

  if (!effectiveOpen) return null

  return (
    <div
      data-testid="selfie-sheet-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background:
          'linear-gradient(180deg, var(--ink) 0%, var(--ink) 60%, color-mix(in srgb, var(--ink) 92%, var(--plum) 8%) 100%)',
      }}
    >
      <div
        // biome-ignore lint/a11y/useSemanticElements: native <dialog> doesn't
        // support our env(safe-area-inset-*) + visualViewport layout. role=dialog
        // + aria-modal is the documented fallback.
        role="dialog"
        aria-modal="true"
        aria-label="Take a selfie"
        data-testid="selfie-sheet"
        data-state={stage.kind}
        style={{
          position: 'absolute',
          inset: 0,
          color: 'var(--paper)',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          gap: 14,
          padding:
            'calc(env(safe-area-inset-top) + 14px) 18px calc(var(--safe-bottom, env(safe-area-inset-bottom)) + 18px)',
          overflow: 'hidden',
        }}
      >
        <SelfieSheetHeader stage={stage} onClose={closeAndRecord} />

        {/* Hidden native file input — `capture=user` hints front camera on iOS. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="user"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          data-testid="selfie-file-input"
        />

        <SelfieSheetBody
          stage={stage}
          venueName={venueName}
          drinkType={drinkType}
          onShutter={handleShutter}
        />

        {/* Action row — KEEP closes the sheet instantly, no 'uploading' stage. */}
        <div className="row" style={{ justifyContent: 'center', gap: 12, minHeight: 50 }}>
          {stage.kind === 'preview' ? (
            <>
              <button
                type="button"
                className="btn secondary"
                onClick={handleRetake}
                aria-label="Retake selfie"
                data-testid="selfie-retake"
                style={{ flex: 1, maxWidth: 160 }}
              >
                Retake
              </button>
              <button
                type="button"
                className="btn pink"
                onClick={handleKeep}
                aria-label="Keep selfie"
                data-testid="selfie-keep"
                style={{ flex: 1, maxWidth: 180 }}
              >
                <Icon name="check" size={18} /> Keep it
              </button>
            </>
          ) : stage.kind === 'error' ? (
            <button
              type="button"
              className="btn pink"
              onClick={handleRetryFromError}
              data-testid="selfie-retry"
              style={{ minWidth: 160 }}
            >
              Try again
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function SelfieSheetHeader({ stage, onClose }: { stage: Stage; onClose: () => void }) {
  const showExpSticker = stage.kind === 'preview'
  return (
    <div className="row-between">
      <button
        type="button"
        className="icon-btn"
        onClick={onClose}
        aria-label="Close selfie sheet without saving"
        data-testid="selfie-close"
        style={{
          background: 'var(--paper)',
          color: 'var(--ink)',
        }}
      >
        <Icon name="close" size={20} />
      </button>
      {showExpSticker ? (
        <span
          className="sticker"
          style={{
            background: 'var(--pink)',
            color: 'var(--paper)',
            borderColor: 'var(--paper)',
          }}
        >
          1 / 24 exp.
        </span>
      ) : (
        // Reserve the slot so the close button stays anchored to the left
        // without the layout jumping when the sticker appears in preview.
        <span aria-hidden style={{ width: 1, height: 1 }} />
      )}
    </div>
  )
}

function SelfieSheetBody({
  stage,
  venueName,
  drinkType,
  onShutter,
}: {
  stage: Stage
  venueName: string | undefined
  drinkType: string | undefined
  onShutter: () => void
}) {
  // Ready: big shutter button. User must tap it. We don't auto-fire — iOS
  // Safari requires the .click() to be inside a synchronous user-gesture
  // descendant, which only the button's onClick handler is.
  if (stage.kind === 'ready') {
    const drinkLabel = drinkType ?? 'drink'
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          minHeight: 0,
          gap: 20,
          padding: '0 24px',
          textAlign: 'center',
        }}
        data-testid="selfie-ready"
      >
        <div className="body-sm" style={{ color: 'var(--paper)', opacity: 0.7 }}>
          quick selfie?
        </div>
        <button
          type="button"
          onClick={onShutter}
          data-testid="selfie-shutter"
          aria-label={`Take a selfie for your ${drinkLabel}`}
          style={{
            display: 'grid',
            placeItems: 'center',
            width: 144,
            height: 144,
            borderRadius: 999,
            border: '4px solid var(--paper)',
            background: 'var(--pink)',
            color: 'var(--paper)',
            fontFamily: 'var(--font-display)',
            fontSize: 22,
            lineHeight: 1,
            textTransform: 'lowercase',
            boxShadow: '0 10px 0 rgba(0,0,0,0.35)',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 44 }} aria-hidden>
            📷
          </span>
        </button>
        <div
          className="body"
          style={{ color: 'var(--paper)', fontSize: 18, maxWidth: 260, lineHeight: 1.25 }}
        >
          snap a {drinkLabel}?
        </div>
      </div>
    )
  }

  if (stage.kind === 'capturing') {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          minHeight: 0,
          gap: 12,
          padding: '0 24px',
          textAlign: 'center',
        }}
        data-testid="selfie-capturing"
      >
        <div
          aria-hidden
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            border: '3px solid rgba(255,255,255,0.18)',
            borderTopColor: 'var(--pink)',
            animation: 'selfie-spin 720ms linear infinite',
          }}
        />
        <div className="body-sm" style={{ color: 'var(--paper)', opacity: 0.7 }}>
          opening camera…
        </div>
        <style>{'@keyframes selfie-spin { to { transform: rotate(360deg); } }'}</style>
      </div>
    )
  }

  if (stage.kind === 'preview') {
    const caption = venueName ? `${venueName} · ${formatTime(new Date())}` : formatTime(new Date())
    return (
      <div
        style={{ display: 'grid', placeItems: 'center', padding: '8px 0', minHeight: 0 }}
        data-testid="selfie-preview"
        data-filtered={stage.filtered ? 'true' : 'false'}
      >
        <div className="polaroid-in" style={{ width: 'min(260px, 70vw)' }}>
          <Polaroid
            src={stage.previewUrl}
            alt="your selfie preview"
            caption={caption}
            tilt="l"
            imgProps={{
              'data-testid': 'polaroid-img',
              'data-filtered': stage.filtered ? 'true' : 'false',
            }}
          />
        </div>
        <style>{`
          @keyframes polaroid-in {
            from { transform: rotate(6deg) translateY(18px) scale(0.94); opacity: 0; }
            to   { transform: rotate(-1.5deg) translateY(0) scale(1); opacity: 1; }
          }
          .polaroid-in {
            animation: polaroid-in 360ms cubic-bezier(0.2, 1.4, 0.4, 1) both;
          }
          @media (prefers-reduced-motion: reduce) {
            .polaroid-in { animation: none; }
          }
        `}</style>
      </div>
    )
  }

  if (stage.kind === 'error') {
    return (
      <div
        style={{
          display: 'grid',
          gap: 14,
          placeItems: 'center',
          padding: '8px 24px',
          textAlign: 'center',
        }}
        data-testid="selfie-error"
      >
        <p className="body" style={{ color: 'var(--paper)', maxWidth: 260 }}>
          {stage.message}
        </p>
      </div>
    )
  }

  // idle — sheet just opened but nothing rendered yet (transient).
  return null
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
